import { useState } from 'react';
import { toast } from 'sonner';
import { sourcingAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Search, History, Radio } from 'lucide-react';
import { CLASSIFICATION_BADGE, TIER_BADGE, OUTCOME_BADGE, badgeClass } from './sourcingUtils';

function formatScannedAt(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  return isNaN(d) ? 'never' : d.toLocaleString();
}

function rfqBadgeText(badge) {
  if (!badge) return null;
  if (!badge.visible) return 'RFQ already sent recently';
  const date = (badge.sent_at || '').slice(0, 10);
  const extra = badge.extra_count > 0 ? ` +${badge.extra_count} more` : '';
  return `RFQ sent ${date} by ${badge.sent_by_name || 'someone'}${extra}`;
}

export default function SkuSearchTab() {
  const { canAccess } = useAuth();
  const canCreate = canAccess('SOURCING', 'CREATE');
  const [skuId, setSkuId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveResult, setLiveResult] = useState(null);

  const search = async () => {
    if (!skuId.trim()) return;
    setLoading(true);
    setNotFound(false);
    setHistory(null);
    setLiveResult(null);
    try {
      const res = await sourcingAPI.getPart(skuId.trim());
      setDetail(res.data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error('Failed to load SKU detail');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const searchLive = async () => {
    if (!skuId.trim()) return;
    setLiveLoading(true);
    try {
      const res = await sourcingAPI.searchLive(skuId.trim());
      setLiveResult(res.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Live BrokerBin search failed');
    } finally {
      setLiveLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await sourcingAPI.supplyDemandHistory(skuId.trim());
      setHistory(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load supply/demand history');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sourcing-sku-search-tab">
      <div className="flex gap-2">
        <Input placeholder="Enter a SKU to see the latest vendor listings, variants, RFQ history and Hot List figures"
          value={skuId} onChange={(e) => setSkuId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
          data-testid="sku-search-input" />
        <Button onClick={search} disabled={loading} aria-label="Search SKU" data-testid="sku-search-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>

      {notFound && (
        <Card className="rounded-sm" data-testid="sku-search-not-found">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">No sourcing part found for "{skuId}".</CardContent>
        </Card>
      )}

      {detail && (
        <div className="space-y-4" data-testid="sku-search-result">
          <Card className="rounded-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-mono">{detail.part.sku_id}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={badgeClass(TIER_BADGE, detail.hot?.tier)} data-testid="sku-search-hot-tier">{detail.hot?.tier || 'NORMAL'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-muted-foreground">Search Part</p><p className="font-mono">{detail.part.search_part || '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Sales Change</p><p>{detail.hot?.sales_change_pct ?? '—'}%</p></div>
              <div><p className="text-xs text-muted-foreground">Distinct Customers</p><p>{detail.hot?.distinct_customers ?? '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Cover Days</p><p>{detail.hot?.cover_days ?? '—'}</p></div>
            </CardContent>
          </Card>

          <Card className="rounded-sm" data-testid="sku-search-listings">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-sm">Vendor Listings</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="sku-search-last-scanned">
                  Last scanned {formatScannedAt(detail.last_scanned_at)}
                </p>
              </div>
              {canCreate && (
                <Button size="sm" variant="outline" onClick={searchLive} disabled={liveLoading} data-testid="sku-search-live-btn">
                  {liveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Radio className="h-3.5 w-3.5 mr-1.5" />} Search BrokerBin live
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0 divide-y divide-border">
              {detail.listings.map((l) => (
                <div key={l.id} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid={`sku-search-listing-${l.id}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{l.company_name_raw}</p>
                    <p className="text-xs text-muted-foreground">{l.condition} · Qty {l.qty} · {l.country || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {l.below_min_qty && (
                      <Badge variant="outline" className="text-[11px] border-amber-500/40 bg-amber-500/10 text-amber-700" data-testid={`sku-search-low-qty-${l.id}`}>
                        Low qty (below {l.min_qty_threshold ?? 10})
                      </Badge>
                    )}
                    {l.rfq_badge && (
                      <Badge variant="outline" className="text-[11px]" data-testid={`sku-search-rfq-badge-${l.id}`}>
                        {rfqBadgeText(l.rfq_badge)}
                        {l.rfq_badge.visible && l.rfq_badge.outcome && ` · ${l.rfq_badge.outcome.replace('_', ' ')}`}
                      </Badge>
                    )}
                    {l.scorecard && l.scorecard.rfqs_sent > 0 && (
                      <Badge variant="outline" className="text-[11px]" data-testid={`sku-search-scorecard-${l.id}`}>
                        {Math.round(l.scorecard.reply_rate * 100)}% reply
                        {l.scorecard.last_5_no_reply && <span className="text-destructive ml-1">· 5 no-reply in a row</span>}
                      </Badge>
                    )}
                    <Badge variant="outline" className={badgeClass(CLASSIFICATION_BADGE, l.classification)}>{l.classification}</Badge>
                  </div>
                </div>
              ))}
              {detail.listings.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No vendor listings scanned yet.</div>}
            </CardContent>
            {liveResult !== null && (
              <div className="border-t border-border p-3" data-testid="sku-search-live-results">
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Radio className="h-3 w-3" />Live BrokerBin results (not saved)
                </p>
                {liveResult.length === 0 && <p className="text-sm text-muted-foreground">No live results right now.</p>}
                {liveResult.map((row, i) => (
                  <div key={i} className="py-1.5 flex items-center justify-between gap-3 text-sm border-b border-border/50 last:border-0" data-testid={`sku-search-live-row-${i}`}>
                    <span className="truncate font-medium">{row.company}</span>
                    <span className="text-xs text-muted-foreground">{row.condition} · Qty {row.qty} · {row.country || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="rounded-sm" data-testid="sku-search-supply-demand">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm">Supply / Demand History</CardTitle>
              <Button size="sm" variant="outline" onClick={loadHistory} disabled={historyLoading} data-testid="sku-search-history-btn">
                {historyLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <History className="h-3.5 w-3.5 mr-1.5" />} Load History
              </Button>
            </CardHeader>
            <CardContent>
              {!history && <p className="text-sm text-muted-foreground">Click to fetch the live BrokerBin history for this part (Production only).</p>}
              {history && !history.available && <p className="text-sm text-muted-foreground" data-testid="sku-search-history-unavailable">{history.message}</p>}
              {history && history.available && (
                <pre className="text-xs overflow-x-auto" data-testid="sku-search-history-data">{JSON.stringify(history, null, 2)}</pre>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-sm" data-testid="sku-search-cooldowns">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Active Cooldowns</CardTitle></CardHeader>
            <CardContent className="p-0 divide-y divide-border">
              {detail.cooldowns.map((c) => (
                <div key={c.id} className="p-3 text-sm" data-testid={`sku-search-cooldown-${c.id}`}>
                  Sent on {(c.sent_at || '').slice(0, 10)} by {c.sent_by_name || 'someone'} · Qty {c.qty}
                </div>
              ))}
              {detail.cooldowns.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No active cooldowns for this part.</div>}
            </CardContent>
          </Card>

          <Card className="rounded-sm" data-testid="sku-search-rfq-history">
            <CardHeader className="pb-2"><CardTitle className="text-sm">RFQ History</CardTitle></CardHeader>
            <CardContent className="p-0 divide-y divide-border">
              {detail.rfq_history.map((r) => (
                <div key={r.id} className="p-3 flex items-center justify-between text-sm" data-testid={`sku-search-rfq-${r.id}`}>
                  <span>{(r.sent_at || '').slice(0, 10)} · {r.subject}</span>
                  <Badge variant="outline">{r.status}</Badge>
                </div>
              ))}
              {detail.rfq_history.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No RFQs sent for this part yet.</div>}
            </CardContent>
          </Card>

          <Card className="rounded-sm" data-testid="sku-search-rfq-30d">
            <CardHeader className="pb-2"><CardTitle className="text-sm">RFQs sent for this SKU (last 30 days)</CardTitle></CardHeader>
            <CardContent className="p-0 divide-y divide-border">
              {(detail.rfq_lines_30d || []).map((e) => (
                <div key={e.rfq_id} className="p-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm" data-testid={`sku-search-rfq-30d-${e.rfq_id}`}>
                  {e.visible ? (
                    <>
                      <span className="truncate font-medium">{e.company_name}</span>
                      <span className="text-xs text-muted-foreground">Sent by {e.sent_by_name || 'someone'} · {(e.sent_at || '').slice(0, 10)} · Qty {e.qty}</span>
                      <span className="text-xs">{e.price_sent ? `Our price: $${e.price_sent}` : 'No price shared'}</span>
                      {e.outcome && <Badge variant="outline" className={badgeClass(OUTCOME_BADGE, e.outcome)}>{e.outcome.replace('_', ' ')}</Badge>}
                    </>
                  ) : (
                    <>
                      <span className="truncate font-medium">{e.company_name}</span>
                      <span className="text-xs text-muted-foreground" data-testid={`sku-search-rfq-30d-restricted-${e.rfq_id}`}>
                        RFQ already sent recently · {(e.sent_at || '').slice(0, 10)}
                      </span>
                    </>
                  )}
                </div>
              ))}
              {(detail.rfq_lines_30d || []).length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No RFQs sent for this SKU in the last 30 days.</div>}
            </CardContent>
          </Card>

          {detail.variants.length > 0 && (
            <Card className="rounded-sm" data-testid="sku-search-variants">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Variant Matches</CardTitle></CardHeader>
              <CardContent className="p-0 divide-y divide-border">
                {detail.variants.map((v) => (
                  <div key={v.id} className="p-3 flex items-center justify-between text-sm" data-testid={`sku-search-variant-${v.id}`}>
                    <span className="font-mono">{v.variant_text}</span>
                    <Badge variant="outline">{v.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
